import { Box, Button, SpaceBetween, TextFilter } from "@amzn/awsui-components-react";
import { FC, useEffect, useMemo, useState } from "react";
import { MapEngineHandle } from "../../react";

export interface LayersPanelProps {
    handle: MapEngineHandle | null;
}

/**
 * Lists the current style's layers with per-layer show/hide toggles, plus
 * Show/Hide all and a filter. Drives the map via engine.setLayerVisibility /
 * getLayerIds (no 1.0 store coupling). Ported in spirit from 1.0 MapLayers.
 */
const LayersPanel: FC<LayersPanelProps> = ({ handle }) => {
    const engine = handle?.engine() ?? null;
    const [layerIds, setLayerIds] = useState<string[]>([]);
    const [hidden, setHidden] = useState<Set<string>>(new Set());
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (engine) setLayerIds(engine.getLayerIds());
    }, [engine]);

    const visible = useMemo(
        () => layerIds.filter((id) => id.toLowerCase().includes(filter.toLowerCase())),
        [layerIds, filter]
    );

    const setVisibility = (id: string, show: boolean) => {
        engine?.setLayerVisibility(id, show);
        setHidden((prev) => {
            const next = new Set(prev);
            if (show) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const setAll = (show: boolean) => {
        layerIds.forEach((id) => engine?.setLayerVisibility(id, show));
        setHidden(show ? new Set() : new Set(layerIds));
    };

    if (!engine) return <Box>Map not ready.</Box>;

    return (
        <SpaceBetween size="s">
            <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setAll(true)}>Show all</Button>
                <Button onClick={() => setAll(false)}>Hide all</Button>
            </SpaceBetween>
            <TextFilter
                filteringText={filter}
                filteringPlaceholder="Filter layers"
                onChange={({ detail }) => setFilter(detail.filteringText)}
            />
            <Box variant="small" color="text-status-inactive">
                {visible.length}/{layerIds.length} layers
            </Box>
            <div>
                {visible.map((id) => {
                    const isVisible = !hidden.has(id);
                    return (
                        <div
                            key={id}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "4px 0",
                                borderBottom: "1px solid #f0f0f0",
                            }}
                        >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {id}
                            </span>
                            <Button
                                variant="inline-icon"
                                iconName={isVisible ? "view-full" : "status-stopped"}
                                ariaLabel={isVisible ? `Hide ${id}` : `Show ${id}`}
                                onClick={() => setVisibility(id, !isVisible)}
                            />
                        </div>
                    );
                })}
            </div>
        </SpaceBetween>
    );
};

export default LayersPanel;
